import { useState, type FormEvent } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { getIcon, AVAILABLE_ICONS } from "@/lib/icon-map";
import {
  useScenarios,
  type CreateScenarioData,
  type ScenarioData,
} from "@/hooks/use-scenarios";
import {
  useToolboxItems,
  type CreateToolboxItemData,
  type ToolboxItemData,
} from "@/hooks/use-toolbox-items";

const scenarioThemes = ["rose", "teal", "amber", "blue", "purple", "emerald", "slate"];
const toolboxCategories = ["stage", "pain", "intervention"] as const;
type ToolboxCategory = (typeof toolboxCategories)[number];

interface ScenarioDraft {
  id?: string;
  title: string;
  description: string;
  iconName: string;
  colorTheme: string;
}

interface ToolboxDraft {
  id?: string;
  title: string;
  defaultDescription: string;
  iconName: string;
  category: ToolboxCategory;
}

const emptyScenarioDraft: ScenarioDraft = {
  title: "",
  description: "",
  iconName: "Coffee",
  colorTheme: "slate",
};

const emptyToolboxDraft: ToolboxDraft = {
  title: "",
  defaultDescription: "",
  iconName: "HelpCircle",
  category: "stage",
};

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "The change could not be saved.";
}

function toSlug(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "item";
}

export function PlatformContentManager() {
  const {
    scenarios,
    isLoading: scenariosLoading,
    isError: scenariosError,
    createScenarioAsync,
    updateScenarioAsync,
    deleteScenarioAsync,
    isCreating: isCreatingScenario,
    isUpdating: isUpdatingScenario,
    isDeleting: isDeletingScenario,
  } = useScenarios();
  const {
    items,
    isLoading: toolboxLoading,
    isError: toolboxError,
    createItemAsync,
    updateItemAsync,
    deleteItemAsync,
    isCreating: isCreatingItem,
    isUpdating: isUpdatingItem,
    isDeleting: isDeletingItem,
  } = useToolboxItems();

  const [scenarioDraft, setScenarioDraft] = useState<ScenarioDraft | null>(null);
  const [toolboxDraft, setToolboxDraft] = useState<ToolboxDraft | null>(null);
  const [notice, setNotice] = useState("");
  const scenarioSaving = isCreatingScenario || isUpdatingScenario || isDeletingScenario;
  const toolboxSaving = isCreatingItem || isUpdatingItem || isDeletingItem;

  const saveScenario = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!scenarioDraft || !scenarioDraft.title.trim()) return;
    setNotice("");

    try {
      const fields: Omit<CreateScenarioData, "typeId"> = {
        title: scenarioDraft.title.trim(),
        description: scenarioDraft.description.trim(),
        iconName: scenarioDraft.iconName,
        colorTheme: scenarioDraft.colorTheme,
      };
      if (scenarioDraft.id) {
        await updateScenarioAsync({ id: scenarioDraft.id, ...fields });
        setNotice("Scenario updated.");
      } else {
        await createScenarioAsync({
          ...fields,
          typeId: `scenario-${toSlug(fields.title)}-${Date.now()}`,
          sortOrder: scenarios.length,
        });
        setNotice("Scenario added.");
      }
      setScenarioDraft(null);
    } catch (error) {
      setNotice(getErrorMessage(error));
    }
  };

  const saveToolboxItem = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!toolboxDraft || !toolboxDraft.title.trim()) return;
    setNotice("");

    try {
      const fields: Omit<CreateToolboxItemData, "typeId"> = {
        title: toolboxDraft.title.trim(),
        defaultDescription: toolboxDraft.defaultDescription.trim(),
        iconName: toolboxDraft.iconName,
        category: toolboxDraft.category,
      };
      if (toolboxDraft.id) {
        await updateItemAsync({ id: toolboxDraft.id, ...fields });
        setNotice("Toolbox item updated.");
      } else {
        const categoryItems = items.filter((item) => item.category === toolboxDraft.category);
        await createItemAsync({
          ...fields,
          typeId: `${toolboxDraft.category}-${toSlug(fields.title)}-${Date.now()}`,
          sortOrder: categoryItems.length,
        });
        setNotice("Toolbox item added.");
      }
      setToolboxDraft(null);
    } catch (error) {
      setNotice(getErrorMessage(error));
    }
  };

  const removeScenario = async (scenario: ScenarioData) => {
    if (scenarios.length <= 1 || !window.confirm(`Delete the “${scenario.title}” scenario?`)) return;
    setNotice("");
    try {
      await deleteScenarioAsync(scenario.id);
      setNotice("Scenario deleted.");
    } catch (error) {
      setNotice(getErrorMessage(error));
    }
  };

  const removeToolboxItem = async (item: ToolboxItemData) => {
    if (!window.confirm(`Delete the “${item.title}” toolbox item?`)) return;
    setNotice("");
    try {
      await deleteItemAsync(item.id);
      setNotice("Toolbox item deleted.");
    } catch (error) {
      setNotice(getErrorMessage(error));
    }
  };

  return (
    <div className="mt-8 space-y-8">
      {notice && (
        <p role="status" className="rounded-lg border border-slate-700 bg-slate-900 px-4 py-3 text-sm text-slate-300">
          {notice}
        </p>
      )}
      <section className="rounded-2xl border border-slate-800 bg-slate-900/70 p-6" aria-labelledby="scenario-admin-title">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 id="scenario-admin-title" className="text-lg font-medium text-white">Lifecycle scenarios</h2>
            <p className="mt-1 text-sm text-slate-400">Manage the scenario choices shown in the Lifecycle Lab.</p>
          </div>
          {!scenarioDraft && (
            <Button onClick={() => setScenarioDraft({ ...emptyScenarioDraft })} disabled={scenarioSaving}>
              <Plus className="mr-2 h-4 w-4" /> Add scenario
            </Button>
          )}
        </div>

        {scenariosLoading ? (
          <p className="mt-5 text-sm text-slate-400">Loading scenarios…</p>
        ) : scenariosError ? (
          <p className="mt-5 text-sm text-rose-300">Scenarios could not be loaded. Refresh and try again.</p>
        ) : (
          <div className="mt-5 grid gap-3">
            {scenarios.map((scenario) => {
              const Icon = getIcon(scenario.iconName);
              return (
                <div key={scenario.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-800 bg-slate-950/60 p-4">
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="rounded-lg bg-slate-800 p-2 text-teal-300"><Icon className="h-4 w-4" /></span>
                    <div className="min-w-0">
                      <h3 className="truncate text-sm font-medium text-white">{scenario.title}</h3>
                      <p className="truncate text-xs text-slate-400">{scenario.description || "No description"} · {scenario.colorTheme || "slate"}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setScenarioDraft({
                      id: scenario.id,
                      title: scenario.title,
                      description: scenario.description || "",
                      iconName: scenario.iconName || "Coffee",
                      colorTheme: scenario.colorTheme || "slate",
                    })} disabled={scenarioSaving}>
                      <Pencil className="mr-2 h-3.5 w-3.5" /> Edit
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => void removeScenario(scenario)}
                      disabled={scenarioSaving || scenarios.length <= 1} title={scenarios.length <= 1 ? "At least one scenario must remain" : undefined}>
                      <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {scenarioDraft && (
          <form onSubmit={saveScenario} className="mt-5 space-y-4 rounded-xl border border-teal-900/70 bg-slate-950/60 p-4">
            <h3 className="font-medium text-white">{scenarioDraft.id ? "Edit scenario" : "Add scenario"}</h3>
            <label className="block space-y-1.5 text-sm text-slate-300">
              <span>Title</span>
              <Input required maxLength={120} value={scenarioDraft.title} onChange={(event) => setScenarioDraft({ ...scenarioDraft, title: event.target.value })} />
            </label>
            <label className="block space-y-1.5 text-sm text-slate-300">
              <span>Description</span>
              <Textarea maxLength={500} value={scenarioDraft.description} onChange={(event) => setScenarioDraft({ ...scenarioDraft, description: event.target.value })} />
            </label>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block space-y-1.5 text-sm text-slate-300">
                <span>Icon</span>
                <select className="h-10 w-full rounded-md border border-slate-700 bg-slate-900 px-3 text-sm text-white" value={scenarioDraft.iconName} onChange={(event) => setScenarioDraft({ ...scenarioDraft, iconName: event.target.value })}>
                  {AVAILABLE_ICONS.map((name) => <option key={name} value={name}>{name}</option>)}
                </select>
              </label>
              <label className="block space-y-1.5 text-sm text-slate-300">
                <span>Color theme</span>
                <select className="h-10 w-full rounded-md border border-slate-700 bg-slate-900 px-3 text-sm text-white" value={scenarioDraft.colorTheme} onChange={(event) => setScenarioDraft({ ...scenarioDraft, colorTheme: event.target.value })}>
                  {scenarioThemes.map((theme) => <option key={theme} value={theme}>{theme}</option>)}
                </select>
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setScenarioDraft(null)} disabled={scenarioSaving}>Cancel</Button>
              <Button type="submit" disabled={scenarioSaving || !scenarioDraft.title.trim()}>{scenarioSaving ? "Saving…" : "Save scenario"}</Button>
            </div>
          </form>
        )}
      </section>

      <section className="rounded-2xl border border-slate-800 bg-slate-900/70 p-6" aria-labelledby="toolbox-admin-title">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 id="toolbox-admin-title" className="text-lg font-medium text-white">Lifecycle toolbox</h2>
            <p className="mt-1 text-sm text-slate-400">Manage the shared lifecycle modes, pain points, and interventions.</p>
          </div>
          {!toolboxDraft && (
            <Button onClick={() => setToolboxDraft({ ...emptyToolboxDraft })} disabled={toolboxSaving}>
              <Plus className="mr-2 h-4 w-4" /> Add toolbox item
            </Button>
          )}
        </div>

        {toolboxLoading ? (
          <p className="mt-5 text-sm text-slate-400">Loading toolbox…</p>
        ) : toolboxError ? (
          <p className="mt-5 text-sm text-rose-300">Toolbox items could not be loaded. Refresh and try again.</p>
        ) : (
          <div className="mt-5 space-y-6">
            {toolboxCategories.map((category) => {
              const categoryItems = items.filter((item) => item.category === category);
              return (
                <div key={category}>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">{category === "stage" ? "Lifecycle modes" : category === "pain" ? "Pain points" : "Interventions"}</h3>
                  {categoryItems.length === 0 ? <p className="rounded-lg border border-dashed border-slate-800 p-3 text-sm text-slate-500">No items in this category.</p> : (
                    <div className="grid gap-2">
                      {categoryItems.map((item) => {
                        const Icon = getIcon(item.iconName);
                        return (
                          <div key={item.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-800 bg-slate-950/60 p-3">
                            <div className="flex min-w-0 items-center gap-3">
                              <Icon className="h-4 w-4 shrink-0 text-teal-300" />
                              <div className="min-w-0">
                                <p className="truncate text-sm font-medium text-white">{item.title}</p>
                                <p className="truncate text-xs text-slate-400">{item.defaultDescription || "No description"}</p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm" onClick={() => setToolboxDraft({
                                id: item.id,
                                title: item.title,
                                defaultDescription: item.defaultDescription || "",
                                iconName: item.iconName || "HelpCircle",
                                category: item.category as ToolboxCategory,
                              })} disabled={toolboxSaving}>
                                <Pencil className="mr-2 h-3.5 w-3.5" /> Edit
                              </Button>
                              <Button variant="destructive" size="sm" onClick={() => void removeToolboxItem(item)} disabled={toolboxSaving}>
                                <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {toolboxDraft && (
          <form onSubmit={saveToolboxItem} className="mt-5 space-y-4 rounded-xl border border-teal-900/70 bg-slate-950/60 p-4">
            <h3 className="font-medium text-white">{toolboxDraft.id ? "Edit toolbox item" : "Add toolbox item"}</h3>
            <label className="block space-y-1.5 text-sm text-slate-300">
              <span>Name</span>
              <Input required maxLength={120} value={toolboxDraft.title} onChange={(event) => setToolboxDraft({ ...toolboxDraft, title: event.target.value })} />
            </label>
            <label className="block space-y-1.5 text-sm text-slate-300">
              <span>Description</span>
              <Textarea maxLength={1000} value={toolboxDraft.defaultDescription} onChange={(event) => setToolboxDraft({ ...toolboxDraft, defaultDescription: event.target.value })} />
            </label>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block space-y-1.5 text-sm text-slate-300">
                <span>Category</span>
                <select className="h-10 w-full rounded-md border border-slate-700 bg-slate-900 px-3 text-sm text-white" value={toolboxDraft.category} onChange={(event) => setToolboxDraft({ ...toolboxDraft, category: event.target.value as ToolboxCategory })}>
                  {toolboxCategories.map((category) => <option key={category} value={category}>{category}</option>)}
                </select>
              </label>
              <label className="block space-y-1.5 text-sm text-slate-300">
                <span>Icon</span>
                <select className="h-10 w-full rounded-md border border-slate-700 bg-slate-900 px-3 text-sm text-white" value={toolboxDraft.iconName} onChange={(event) => setToolboxDraft({ ...toolboxDraft, iconName: event.target.value })}>
                  {AVAILABLE_ICONS.map((name) => <option key={name} value={name}>{name}</option>)}
                </select>
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setToolboxDraft(null)} disabled={toolboxSaving}>Cancel</Button>
              <Button type="submit" disabled={toolboxSaving || !toolboxDraft.title.trim()}>{toolboxSaving ? "Saving…" : "Save toolbox item"}</Button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
